import { useCallback, useEffect, useRef, useState } from 'react';
import { executeCohort } from '../../../../services/cohortService';

const getPathFromRootToNode = (graphState, nodeId) => {
  const nodesById = graphState?.nodesById || {};
  const rootId = graphState?.rootNodeId || null;
  if (!nodeId || !rootId || !nodesById[nodeId]) return null;

  const visited = new Set();
  const path = [];
  let currentId = nodeId;

  while (currentId) {
    if (visited.has(currentId)) return null;
    visited.add(currentId);
    path.push(currentId);
    if (currentId === rootId) break;
    currentId = nodesById[currentId]?.parentId || null;
  }

  if (path[path.length - 1] !== rootId) return null;
  return path.reverse();
};

const buildExecutionPlan = (graphState, targetNodeId = null) => {
  const nodesById = graphState?.nodesById || {};
  const rootId = graphState?.rootNodeId || null;
  if (!rootId || !nodesById[rootId]) return [];

  let allowedIds = null;
  if (targetNodeId) {
    const path = getPathFromRootToNode(graphState, targetNodeId);
    if (!path) return [];
    allowedIds = new Set(path);
  }

  const queue = [rootId];
  const visited = new Set();
  const executionOrder = [];

  while (queue.length > 0) {
    const currentId = queue.shift();
    if (!currentId || visited.has(currentId)) continue;
    if (allowedIds && !allowedIds.has(currentId)) continue;
    visited.add(currentId);
    executionOrder.push(currentId);

    if (targetNodeId && currentId === targetNodeId) continue;

    const children = nodesById[currentId]?.childIds || [];
    children.forEach((childId) => {
      if (!visited.has(childId)) queue.push(childId);
    });
  }

  return executionOrder;
};

// Kein "0 ms" bei einer aelteren Server-Instanz ohne `dauerMs` (#795 AK3): eine
// erfundene Messung waere schlimmer als keine Angabe. Die Aufteilung in
// Uebersetzung/Datenbank steht, wenn vorhanden, direkt neben der Gesamtzahl im
// selben Protokolleintrag (#795 AK2) -- eine langsame Uebersetzung ist ein
// Codeproblem, eine langsame Datenbank ein Index-/Datenmengenproblem.
const formatDauerMs = (dauerMs) => {
  if (!dauerMs || !Number.isFinite(dauerMs.gesamt)) return '';
  const breakdown =
    Number.isFinite(dauerMs.uebersetzung) && Number.isFinite(dauerMs.datenbank)
      ? `, translation ${dauerMs.uebersetzung.toFixed(1)} ms, database ${dauerMs.datenbank.toFixed(1)} ms`
      : '';
  return ` (${dauerMs.gesamt.toFixed(1)} ms total${breakdown})`;
};

/**
 * Runs graph execution against the backend (POST /cohorts/{id}/execute, #E7.1)
 * and applies the returned node statuses/counts. No client-side estimation:
 * a node without a usable result becomes 'error', never a guessed count (AK2).
 */
export const useGraphExecution = ({
  graphState,
  graphStateRef,
  applyGraphAction,
  showFeedback,
  graphActions,
  onPrepareExecution,
  cohortId,
}) => {
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [executionTargetNodeId, setExecutionTargetNodeId] = useState(null);
  const [executionMode, setExecutionMode] = useState('full');
  // AK5: bis #E7.5 den openEHR-Pfad des Servers liefert, ist 'omop' die einzige
  // Option, die die Oberflaeche tatsaechlich anbietet (siehe CohortExecutionPanel).
  const [executionBackend, setExecutionBackend] = useState('omop');
  const [executionLogs, setExecutionLogs] = useState([]);
  const [showExecutionLog, setShowExecutionLog] = useState(false);
  const [lastExecutionAt, setLastExecutionAt] = useState(graphState.metadata?.lastExecutedAt || null);
  const executionRunIdRef = useRef(0);

  useEffect(() => {
    setLastExecutionAt(graphState.metadata?.lastExecutedAt || null);
  }, [graphState.metadata]);

  const appendExecutionLog = useCallback((message) => {
    const timestamp = new Date().toLocaleTimeString();
    setExecutionLogs((previous) => [`[${timestamp}] ${message}`, ...previous].slice(0, 24));
  }, []);

  const runGraphExecution = useCallback(
    async (targetNodeId = null, mode = 'full') => {
      if (isExecuting) return;

      if (!cohortId) {
        showFeedback('Save the cohort before executing it.', 'warning');
        return;
      }

      const currentGraph = graphStateRef.current;
      const nodeIds = Object.keys(currentGraph.nodesById || {});
      if (nodeIds.length === 0) {
        showFeedback('Please create or load a graph before executing.', 'warning');
        return;
      }

      const executionPlan = buildExecutionPlan(currentGraph, targetNodeId);
      if (executionPlan.length === 0) {
        showFeedback(
          'Could not build an execution plan for the selected node. Check parent-child links and breakpoint.',
          'error',
        );
        return;
      }

      const runId = executionRunIdRef.current + 1;
      executionRunIdRef.current = runId;

      setIsExecuting(true);
      setExecutionProgress(0);
      setExecutionTargetNodeId(targetNodeId || null);
      setExecutionMode(mode);
      setExecutionLogs([]);
      onPrepareExecution?.();

      const backendLabel = executionBackend.toUpperCase();
      if (mode === 'breakpoint' && targetNodeId) {
        appendExecutionLog(`Started execution to breakpoint node ${targetNodeId} (${backendLabel}).`);
      } else if (targetNodeId) {
        appendExecutionLog(`Started partial execution to node ${targetNodeId} (${backendLabel}).`);
      } else {
        appendExecutionLog(`Started full graph execution (${backendLabel}).`);
      }

      const resetNodesById = {};
      nodeIds.forEach((nodeId) => {
        const node = currentGraph.nodesById[nodeId];
        resetNodesById[nodeId] = {
          ...node,
          status: 'pending',
          patientCount: null,
          isExecuted: false,
        };
      });

      applyGraphAction(
        {
          type: graphActions.SET_GRAPH,
          payload: {
            ...currentGraph,
            nodesById: resetNodesById,
            lastError: null,
          },
        },
        { recordHistory: true },
      );

      executionPlan.forEach((nodeId) => {
        applyGraphAction(
          {
            type: graphActions.UPDATE_NODE,
            payload: {
              nodeId,
              patch: { status: 'executing', patientCount: null, isExecuted: false },
            },
          },
          { recordHistory: false },
        );
      });

      try {
        let result = null;
        try {
          result = await executeCohort(cohortId, { targetNodeId, mode, backend: executionBackend });
        } catch (error) {
          if (executionRunIdRef.current !== runId) return;

          const message = error instanceof Error ? error.message : 'Cohort execution failed.';
          executionPlan.forEach((nodeId) => {
            applyGraphAction(
              {
                type: graphActions.UPDATE_NODE,
                payload: {
                  nodeId,
                  patch: { status: 'error', patientCount: null, isExecuted: false },
                },
              },
              { recordHistory: false },
            );
          });
          appendExecutionLog(`Execution failed: ${message}`);
          showFeedback(`Cohort execution failed: ${message}`, 'error');
          return;
        }

        if (executionRunIdRef.current !== runId) return;

        const resultsByNodeId = new Map((result?.nodes || []).map((node) => [node.nodeId, node]));
        // Kein Rueckfall auf das angefragte Backend / die Browser-Uhrzeit: fehlt
        // die Angabe in der Antwort, ist sie unbekannt -- alles andere waere eine
        // erfundene Angabe wie die entfernte Schaetzzahl (Review Runde 1, B4).
        const source = result?.backend || 'unknown';
        const computedAt = result?.executedAt ? new Date(result.executedAt).toLocaleTimeString() : 'unknown time';
        const durationMs = result?.dauerMs || null;

        // Ein einziges Dispatch fuer alle Knoten dieses Laufs statt eines pro
        // Knoten: `graphStateRef.current` synct erst nach dem naechsten Commit
        // (useEffect) nach -- mehrere applyGraphAction()-Aufrufe hintereinander,
        // gefolgt von einem Lesen von graphStateRef.current fuer ein Metadaten-
        // Update, wuerden die gerade erst verschickten Knotenergebnisse mit
        // diesem veralteten Stand wieder ueberschreiben.
        const finalNodesById = { ...graphStateRef.current.nodesById };

        executionPlan.forEach((nodeId, index) => {
          const node = currentGraph.nodesById[nodeId];
          const nodeResult = resultsByNodeId.get(nodeId);
          let status = nodeResult?.status || 'error';
          let patientCount = status === 'completed' ? nodeResult.patientCount : null;
          let message = nodeResult?.message || '';
          // Verteidigung neben der Normalisierung in cohortService.js: ein
          // 'completed' ohne verwertbare Zahl darf nie bis zu `.toLocaleString()`
          // durchrutschen (Review Runde 1, B1) -- ein Feld, das "immer da ist",
          // war schon einmal nicht da (dod.md).
          if (status === 'completed' && !Number.isFinite(patientCount)) {
            status = 'error';
            patientCount = null;
            message = message || 'No usable patient count in the response.';
          }

          finalNodesById[nodeId] = {
            ...finalNodesById[nodeId],
            status,
            patientCount,
            isExecuted: status === 'completed',
          };

          const label = node?.name || nodeId;
          if (status === 'completed') {
            appendExecutionLog(
              `${label}: ${patientCount.toLocaleString()} patients (source: ${source}, computed ${computedAt}).`,
            );
          } else if (status === 'unmapped') {
            appendExecutionLog(`${label}: criterion could not be translated by the server (source: ${source}).`);
          } else {
            appendExecutionLog(`${label}: execution failed${message ? ` (${message})` : ''} (source: ${source}).`);
          }

          setExecutionProgress(Math.round(((index + 1) / executionPlan.length) * 100));
        });

        if (mode === 'breakpoint' && targetNodeId) {
          appendExecutionLog(`Breakpoint reached at node ${targetNodeId}.`);
        }

        // Eine leere oder unvollstaendige nodes-Liste (auch ein 204) laesst jeden
        // Knoten auf 'error' fallen -- das ist ein fehlgeschlagener Lauf, kein
        // Erfolg (Review Runde 1, B3). 'unmapped' zaehlt nicht als Fehlschlag: der
        // Server hat geantwortet, nur das Kriterium war nicht uebersetzbar (AK3).
        const anyUsableResult = executionPlan.some((nodeId) => finalNodesById[nodeId].status !== 'error');

        // Kein Rueckfall auf die Browser-Uhrzeit, wenn die Antwort keinen
        // Zeitpunkt liefert -- sonst zeigt der Verlauf eine erfundene Angabe
        // statt der zuletzt bekannten (Review Runde 1, B4).
        const finishedAtIso = result?.executedAt || null;
        if (finishedAtIso) {
          setLastExecutionAt(finishedAtIso);
        }

        applyGraphAction(
          {
            type: graphActions.SET_GRAPH,
            payload: {
              ...graphStateRef.current,
              nodesById: finalNodesById,
              metadata: {
                ...graphStateRef.current.metadata,
                lastExecutedAt: finishedAtIso || graphStateRef.current.metadata?.lastExecutedAt || null,
              },
            },
          },
          { recordHistory: false },
        );

        if (anyUsableResult) {
          appendExecutionLog(`Execution completed${formatDauerMs(durationMs)}.`);
          showFeedback(
            mode === 'breakpoint' && targetNodeId
              ? `Execution completed at breakpoint node ${targetNodeId}.`
              : targetNodeId
                ? `Execution completed up to node ${targetNodeId}.`
                : 'Graph execution completed.',
            'success',
          );
        } else {
          appendExecutionLog(
            `Execution failed: the server returned no usable result for any node${formatDauerMs(durationMs)}.`,
          );
          showFeedback('Cohort execution failed: the server returned no usable result for any node.', 'error');
        }
      } finally {
        if (executionRunIdRef.current === runId) {
          setIsExecuting(false);
          setExecutionTargetNodeId(null);
          setExecutionMode('full');
        }
      }
    },
    [
      appendExecutionLog,
      applyGraphAction,
      cohortId,
      executionBackend,
      graphActions.SET_GRAPH,
      graphActions.UPDATE_NODE,
      graphStateRef,
      isExecuting,
      onPrepareExecution,
      showFeedback,
    ],
  );

  const executeGraph = useCallback(() => {
    runGraphExecution(null, 'full');
  }, [runGraphExecution]);

  const executeToHere = useCallback(
    (nodeId) => {
      runGraphExecution(nodeId, 'node');
    },
    [runGraphExecution],
  );

  const executeToBreakpoint = useCallback(() => {
    const targetNodeId = graphStateRef.current.metadata?.breakpointNodeId || null;
    if (!targetNodeId) {
      showFeedback('Set a breakpoint on a node first.', 'warning');
      return;
    }
    runGraphExecution(targetNodeId, 'breakpoint');
  }, [graphStateRef, runGraphExecution, showFeedback]);

  return {
    isExecuting,
    executionProgress,
    executionTargetNodeId,
    executionMode,
    executionBackend,
    setExecutionBackend,
    executionLogs,
    showExecutionLog,
    setShowExecutionLog,
    lastExecutionAt,
    executeGraph,
    executeToHere,
    executeToBreakpoint,
  };
};

export default useGraphExecution;
